import * as pdfjsLib from 'pdfjs-dist';

const MIN_RESUME_TEXT_LENGTH = 50;
const MAX_VISION_PAGES = 4;
const MAX_RENDER_WIDTH = 960;
const JPEG_QUALITY = 0.78;

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const PDF_WORKER_URLS = [
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`,
  `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`,
];

function setPdfWorker(index = 0) {
  pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_URLS[index] || PDF_WORKER_URLS[0];
}

function sortTextItems(items) {
  return items
    .filter((item) => item.str != null && String(item.str).trim() !== '')
    .map((item) => ({
      str: item.str,
      x: item.transform?.[4] ?? 0,
      y: item.transform?.[5] ?? 0,
    }))
    .sort((a, b) => {
      const yDiff = b.y - a.y;
      if (Math.abs(yDiff) > 4) return yDiff;
      return a.x - b.x;
    });
}

function extractPageText(textContent) {
  return sortTextItems(textContent.items)
    .map((item) => item.str)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

async function loadPdf(file) {
  const arrayBuffer = await file.arrayBuffer();
  let lastError;

  for (let attempt = 0; attempt < PDF_WORKER_URLS.length; attempt += 1) {
    setPdfWorker(attempt);
    try {
      const loadingTask = pdfjsLib.getDocument({
        data: arrayBuffer.slice(0),
        useSystemFonts: true,
      });
      return await loadingTask.promise;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error('Could not open PDF file');
}

async function extractPdfTextFromPdf(pdf) {
  let fullText = '';

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const textContent = await page.getTextContent();
    const pageText = extractPageText(textContent);
    if (pageText) {
      fullText += `${pageText}\n`;
    }
  }

  return fullText.trim();
}

async function renderPageToDataUrl(page) {
  const baseViewport = page.getViewport({ scale: 1 });
  const scale = Math.min(2, MAX_RENDER_WIDTH / baseViewport.width);
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({
    canvasContext: context,
    viewport,
  }).promise;

  return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
}

async function extractPdfPageImagesFromPdf(pdf, maxPages = MAX_VISION_PAGES) {
  const pageLimit = Math.min(pdf.numPages, maxPages);
  const images = [];

  for (let i = 1; i <= pageLimit; i++) {
    const page = await pdf.getPage(i);
    images.push(await renderPageToDataUrl(page));
  }

  return images;
}

async function fileToBase64(file) {
  const arrayBuffer = await file.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

/**
 * Extract plain text from a PDF File object.
 */
export async function extractPdfText(file) {
  const pdf = await loadPdf(file);
  return extractPdfTextFromPdf(pdf);
}

/**
 * Extract text from resume file (PDF or plain text).
 */
export async function extractResumeText(file) {
  const content = await extractResumeContent(file);
  if (content.text?.length >= MIN_RESUME_TEXT_LENGTH) {
    return content.text;
  }
  if (content.parseMethod === 'vision') {
    return content.text || '[Scanned resume — parsed visually]';
  }
  throw new Error(
    'Could not extract enough text from this resume. Try a PDF with selectable text or a plain .txt file.'
  );
}

/**
 * Extract resume content for qualification — text layer first, then page images for scanned PDFs.
 */
export async function extractResumeContent(file) {
  if (!file) throw new Error('No file provided');

  const type = file.type || '';
  const name = (file.name || '').toLowerCase();

  if (
    type.startsWith('text/') ||
    name.endsWith('.txt') ||
    name.endsWith('.md')
  ) {
    const text = (await file.text()).trim();
    return { text, images: [], parseMethod: 'text' };
  }

  if (!(type === 'application/pdf' || name.endsWith('.pdf'))) {
    throw new Error('Unsupported file type. Please upload a PDF or text file.');
  }

  try {
    const pdf = await loadPdf(file);
    const text = await extractPdfTextFromPdf(pdf);

    if (text.length >= MIN_RESUME_TEXT_LENGTH) {
      return { text, images: [], parseMethod: 'text' };
    }

    const images = await extractPdfPageImagesFromPdf(pdf);
    if (images.length > 0) {
      return {
        text,
        images,
        parseMethod: 'vision',
      };
    }

    if (text.length > 0) {
      return { text, images: [], parseMethod: 'text' };
    }

    throw new Error('empty_pdf');
  } catch (error) {
    if (error.message !== 'empty_pdf' && error.name !== 'InvalidPDFException') {
      console.warn('Client PDF parse failed, will try server-side extraction:', error);
    }

    const resumePdfBase64 = await fileToBase64(file);
    return {
      text: '',
      images: [],
      resumePdfBase64,
      parseMethod: 'server',
    };
  }
}

export { MIN_RESUME_TEXT_LENGTH, MAX_VISION_PAGES };
